import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { URL_PARAMS } from "@/constants/urlParams";

/**
 * Manages search state with URL synchronization and debounced API querying.
 *
 * Returns three representations of the search value for different use cases:
 * - `searchValue` — immediate, for controlled input display
 * - `debouncedSearchValue` — debounced (500ms), for API calls and React Query keys
 *
 * @example
 * const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();
 *
 * // Input display — use searchValue (immediate)
 * <TableToolbar searchValue={searchValue} onSearchChange={setSearchValue} />
 *
 * // API calls — use debouncedSearchValue (debounced)
 * const { data } = useVacancies({ search: debouncedSearchValue });
 */
export const useSearchSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get(URL_PARAMS.SEARCH) || undefined;
  const [searchValue, setSearchValue] = useState(search);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(search);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      // Skip the URL sync entirely when nothing would change. Without this guard, react-router's
      // setSearchParams still pushes a history entry even when the rebuilt URL is identical to the
      // current one, which breaks browser-back behaviour on pages where the user navigates between
      // sibling routes (e.g. tab-driven reporting levels) within 500ms of mount.
      const currentSearch = searchParams.get(URL_PARAMS.SEARCH) || undefined;
      if (currentSearch === searchValue) return;
      setSearchParams((prev) => {
        const urlParams = new URLSearchParams(prev);
        if (searchValue) {
          urlParams.set(URL_PARAMS.SEARCH, searchValue);
        } else {
          urlParams.delete(URL_PARAMS.SEARCH);
        }
        if (urlParams.has(URL_PARAMS.PAGE)) {
          urlParams.set(URL_PARAMS.PAGE, "1");
        }
        return urlParams;
      });
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchValue, searchParams, setSearchParams]);

  return {
    searchValue,
    debouncedSearchValue,
    setSearchValue: useCallback((value: string) => {
      setSearchValue(value || undefined);
    }, []),
  };
};
